import { FORM_IS_DIRTY_ARE_YOU_SURE_YOU_WANT_TO_LEAVE } from 'mk/autogenerated/translations/form.da3a79c0cd2d1c25446db9af6f593ea7'
import PhotosUploadScreen from 'mk2/apps/forum/containers/PhotosUploadScreen/PhotosUploadScreen';
import Interpolate, { OwnProps as InterpolateProps } from 'mk2/components/Interpolate';
import { FormPageType } from 'mk2/constants';
import { PhotosUploadConfig } from 'mk2/containers/PhotosUpload/PhotosUpload';
import { XHRFormError } from 'mk2/helpers/api';
import { cacheLast } from 'mk2/helpers/cache';
import { mkReduxFormCancelPage, mkReduxFormClosePage, mkReduxFormOpenPage } from 'mk2/helpers/form.actions';
import Loadable from 'mk2/helpers/Loadable';
import { showServerErrorToast, showSuccessToast } from 'mk2/helpers/toasts';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { Logger } from 'mk2/logger';
import { AppState, getFormState, getRequestDeviceMobile } from 'mk2/reducers';
import { redirectInPWASaga } from 'mk2/services/browserHistory';
import * as React from 'react';
import { connect } from 'react-redux';
import { Prompt } from 'react-router';
import {
    FormErrors,
    initialize,
    InjectedFormProps,
    InjectedFormProps as ReduxFormInjectedProps,
    isDirty as formIsDirty,
    reduxForm,
    reset,
    stopSubmit,
    SubmissionError,
} from 'redux-form';
import { delay } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

const StickerField = Loadable({
    loader: () => import('mk2/components/forms/StickerField' /* webpackChunkName: "components.forms.StickerField" */),
    modules: ['mk2/components/forms/StickerField'],
    webpack: () => [ require.resolveWeak('mk2/components/forms/StickerField') ],
});

export type CursorPosition = 'start' | 'end' | 'select';
export type AutoFocus = boolean | CursorPosition;

export interface DjangoFormFieldError {
    code: string;
    message: string;
    messageParams?: {[key: string]: string};
}

export interface DjangoFormErrors {
    [fieldName: string]: DjangoFormFieldError[];
}

export function* handleXHRFormSuccessSaga(
    reduxFormName: string,
    gotoUrl?: string,
    toastSuccessMessage?: React.ReactNode,
    newInitialValues?: any,
) {
    // Stop submit
    yield put(stopSubmit(reduxFormName));

    // Reset form
    yield put(reset(reduxFormName));
    // Wait (max. 3 sec) while the reset makes the form non-dirty (in most cases the form is already non dirty)
    for (let i = 0; i < 30; i++) {
        const formState = yield select(getFormState, reduxFormName);
        if (formIsDirty(reduxFormName)(formState)) {
            yield call(delay, 100);
        } else {
            break;
        }
    }

    if (newInitialValues) {
        yield put(initialize(reduxFormName, newInitialValues));
    }

    // Display toast message
    if (toastSuccessMessage) {
        yield showSuccessToast(toastSuccessMessage);
    }

    if (gotoUrl) {
        yield redirectInPWASaga(gotoUrl);
    }
}

export function* handleXHRFormErrorSaga(reduxFormName: string, error: XHRFormError, logger: Logger) {
    // Bad request - form invalid
    if (error.status === 400) {
        const djFormErrors: DjangoFormErrors = error.response.body.errors;

        const reduxFormErros = {};
        Object.keys(djFormErrors).forEach((fieldName) => {
            const fieldErrors: DjangoFormFieldError[] = djFormErrors[fieldName];

            const formattedMessages: React.ReactChild[] = [];
            fieldErrors.forEach((fieldError) => {
                if (fieldError.messageParams) {
                    const formattedParams: { [name: string]: React.ReactNode } = {};
                    Object.keys(fieldError.messageParams).forEach((name) => {
                        let value: React.ReactChild = fieldError.messageParams[name];
                        if (name === 'url') {
                            value = <a href={value}>{value}</a>;
                        }
                        formattedParams[name] = value;
                    });

                    const props: InterpolateProps = {
                        key: formattedMessages.length,
                        className: 'errormsg',
                        i18nKey: fieldError.message,
                        useDangerouslySetInnerHTML: true,
                        ...formattedParams,
                    };

                    formattedMessages.push(
                        // <Interpolate> generates <span> element with text inside
                        <Interpolate {...props} />,
                    );
                } else {
                    formattedMessages.push(
                        <span
                            key={formattedMessages.length}
                            className="errormsg"
                            dangerouslySetInnerHTML={{ __html: fieldError.message }}
                        />,
                    );
                }
            });

            // error message for one field if span of span:
            // <span class="errors">
            //     <span> error msg1 </span>
            //     <span> error msg2 </span>
            // </span>
            reduxFormErros[fieldName] = <span className="errormsg_list">{...formattedMessages}</span>;
        });

        yield put(stopSubmit(reduxFormName, reduxFormErros));
    } else {
        logger.error(error);
        yield put(stopSubmit(reduxFormName));
        yield showServerErrorToast();
    }
}

interface MKReduxFormConfig<FormData, Props> {
    formName: string;
    photosConfig?: PhotosUploadConfig;
    onValidate: (values: FormData, props: Props) => FormErrors<FormData>;
    onSave: (values: FormData, props: Props) => void;
    onChange?: (newValues: FormData, oldValues: FormData, props: Props) => void;
    disableDirtyCheck?: boolean;
}

// every component where @mkReduxFormNew() decorator is applied must contain these attributes
interface MKReduxFormOwnProps {
}

// state props, ktore posielame dole do obaleneho komponenty
interface MKReduxFormStateProps {
    currentFormPage: FormPageType;
    mobile: boolean;
}

// dispatch props, ktore posielame dole do obaleneho komponenty
interface MKReduxFormDispatchProps {
    onOpenFormPage(formPage: FormPageType);
    onCloseFormPage();
    onCancelFormPage();
}

// interface, ktory sa vklada do React.Component<?? & MKReduxFormInjectedProps> form componentu
export type MKReduxFormInjectedProps = MKReduxFormStateProps & MKReduxFormDispatchProps & ReduxFormInjectedProps;

// aky typ komponentov berie a vracia @mkReduxFormNew dekorator
type MKReduxFormDecorator<Props> =
    (WrappedComponent: React.ComponentClass<Props | Props & MKReduxFormInjectedProps>) =>
        React.ComponentClass<Props>;

export function mkReduxFormNew<FormData, Props extends MKReduxFormOwnProps>(config: MKReduxFormConfig<FormData, Props>): MKReduxFormDecorator<Props> {
    const f = reduxForm({
        form: config.formName,
        persistentSubmitErrors: true,
        onSubmit: (values: FormData, dispatch, props: Props) => {
            const errors = config.onValidate(values, props);
            if (Object.keys(errors).length) {
                return Promise.reject(new SubmissionError(errors));
            }

            // redux-form set isSubmitting based on value returned
            // because we are submiting async, we need to return Promise
            // and because we are submiting using sagas, we fake this promise as never resolved
            return Promise.all([
                Promise.resolve().then(() => {
                    // musi byt v promise, aby sa akcia START_SUBMIT firovala este pred zavolanim onSave()
                    //
                    // Pretoze ked onSave() zavola stopSubmit(), fireje tym akciu STOP_SUBMIT a potom sa
                    // tieto dve akcie udeju v opacnom poradi (najskor STOP_SUBMIT a potom START_SUBMIT)
                    //
                    // To sposobi, ze form ostane v zlom stave (isSubmitting = true)
                    config.onSave(values, props);
                }),
                new Promise(() => {
                    // Never ending (resolved) story :D
                    return;
                }),
            ]);
        },
        onChange: !config.onChange
            ? undefined
            : (newValues: Partial<FormData>, dispatch, props: Props, oldValues: Partial<FormData>) => {
                // vyzera ze redux-form posiela cele FormData a nie iba Partial<FormData>
                config.onChange(newValues as FormData, oldValues as FormData, props);
            },
    });

    const g = (WrappedComponent) => {
        class MKFormHoC extends React.Component<Props & MKReduxFormInjectedProps> {
            public render() {
                const { dirty, currentFormPage } = this.props;

                return (
                    // pouzity fragment a nie div, aby sa flex-layout v parent containeri vztahoval na form fieldy
                    <React.Fragment>
                        {!config.disableDirtyCheck && (
                            <Prompt when={dirty} message={FORM_IS_DIRTY_ARE_YOU_SURE_YOU_WANT_TO_LEAVE} />
                        )}
                        {currentFormPage === FormPageType.Stickers ? (
                                <StickerField
                                    name="sticker"
                                />
                            ) : currentFormPage === FormPageType.Photos ? (
                                    <PhotosUploadScreen
                                        formName={config.formName}
                                        config={config.photosConfig}
                                    />
                                ) : (
                                    <WrappedComponent {...this.props as any} />
                                )
                        }
                    </React.Fragment>
                );
            }
        }
        return MKFormHoC as any;
    };

    return (c) =>
        connect(mkReduxFormMapStateToProps(config.formName), mkReduxFormMapDispatchToProps(config.formName))(
            f(g(c)) as any) as any;
}

export const mkReduxFormMapStateToProps = (formName: string) => (state: AppState): MKReduxFormStateProps => {
    const formState = getFormState(state, formName);

    return {
        currentFormPage: formState.currentFormPage,
        mobile: getRequestDeviceMobile(state),
    };
};

export const mkReduxFormMapDispatchToProps = (formName: string): MapDispatchToPropsObject<MKReduxFormDispatchProps> => ({
    onOpenFormPage: (formPage) => mkReduxFormOpenPage(formName, formPage),
    onCloseFormPage: () => mkReduxFormClosePage(formName),
    onCancelFormPage: () => mkReduxFormCancelPage(formName),
});

export function autoFocusElement(element: HTMLInputElement | HTMLTextAreaElement, autoFocus: boolean | CursorPosition, value: string) {
    if (autoFocus) {
        element.focus();

        if (autoFocus === 'select' || autoFocus === true) {
            element.setSelectionRange(0, value.length);
            element.scrollTop = 0;
        } else if (autoFocus === 'start') {
            element.setSelectionRange(0, 0);
            element.scrollTop = 0;
        } else if (autoFocus === 'end') {
            element.setSelectionRange(value.length, value.length);
            element.scrollTop = 0;
        }
    }
}

export const normalizeToInt = (value) => value && parseInt(value, 10);

export const normalizeToFloat = (value) => value && parseFloat(value);

export const normalizeToDecimal = (value) => value && Math.round(parseFloat(value) * 100) / 100;

const initialValuesCache = cacheLast();

/*
 * This function helps to avoid unnecessary re-renders of react components because of changing
 * initialValues object.
 *
 * In naive case, mapStateToProps() always creates new object for initialValues, e.g.
 *
 * return {
 *     .... some other props
 *     initialValues: {
 *        field1: 'value1'
 *        field2: 'value2'
 * }
 *
 * so we cache the last initialValues object to avoid re-renders.
 */
export function cachedInitialValues<FormData>(formName, initialValues: FormData, ...cacheKeys): FormData {
    return initialValuesCache(initialValues, formName, ...cacheKeys);
}
